import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import type { ReleaseNote } from "@/api/types";
import { Close, ExpandLess, ExpandMore } from "@/components/icons";
import { formatDayRange, noteDateRange, planDialog } from "@/features/whatsNew/logic";
import { ReleaseNoteItem } from "@/features/whatsNew/ReleaseNoteItem";
import { intlLocale } from "@/i18n/format";

type Props = {
  open: boolean;
  notes: ReleaseNote[];
  omitted: number;
  /** The user's previous position: "See all changes" opens the page
   *  with its divider there. */
  since?: string | null;
  /** Every way out: Got it, ✕, Esc, the backdrop (no argument), and
   *  See all changes / Show me (the path to open next). */
  onClose: (then?: string) => void;
  onExited?: () => void;
};

const SECTION_HEADING_SX = {
  textTransform: "uppercase",
  letterSpacing: 0.6,
  fontSize: "0.75rem",
  fontWeight: 700,
  color: "text.secondary",
  mb: 1,
} as const;

const LIST_SX = { listStyle: "none", m: 0, p: 0, display: "grid", gap: 1 } as const;

/** "Repoworks has been updated": what changed since the user's last
 *  visit, after a reload onto a new build. Highlights lead with their
 *  bodies, the rest are titles, fixes wait behind a disclosure, and the
 *  remainder is a count with a way to the full page. */
export function WhatsNewDialog({
  open,
  notes,
  omitted,
  since = null,
  onClose,
  onExited,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down("sm"));
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [fixesOpen, setFixesOpen] = React.useState(false);
  const plan = planDialog(notes, omitted);
  const range = noteDateRange(notes);
  const seeAll = since ? `/app/changelog?since=${encodeURIComponent(since)}` : "/app/changelog";
  const showMe = (link: string) => onClose(link);

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      maxWidth="sm"
      fullWidth
      fullScreen={phone}
      scroll="paper"
      aria-labelledby="whats-new-title"
      aria-describedby="whats-new-intro"
      transitionDuration={reduceMotion ? 0 : undefined}
      slotProps={{ transition: { onExited } }}
    >
      {/* Not DialogTitle: it would take the aria-labelledby id itself,
          and the h2 inside must carry it. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: phone ? "row-reverse" : "row",
          alignItems: "flex-start",
          gap: 1,
          px: phone ? 2 : 3,
          pt: 2,
          pb: 1.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id="whats-new-title" component="h2" variant="h6" sx={{ fontWeight: 700 }}>
            {t("whats_new.title")}
          </Typography>
          <Typography id="whats-new-intro" variant="body2" sx={{ mt: 0.5 }}>
            {t("whats_new.intro")}
          </Typography>
          {range && (
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
              {formatDayRange(range.from, range.to, intlLocale())}
            </Typography>
          )}
        </Box>
        <IconButton
          aria-label={t("common.close")}
          onClick={() => onClose()}
          edge={phone ? "start" : "end"}
          sx={{ width: 44, height: 44, mt: -0.5 }}
        >
          <Close aria-hidden />
        </IconButton>
      </Box>
      <DialogContent
        dividers
        // Scrollable by keyboard, with a visible focus ring.
        tabIndex={0}
        sx={{
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: -2,
          },
        }}
      >
        <Stack sx={{ gap: 2.5 }}>
          {plan.highlights.length > 0 && (
            <Box component="section">
              <Typography component="h3" sx={SECTION_HEADING_SX}>
                {t("whats_new.highlights")}
              </Typography>
              <Stack sx={{ gap: 1.25 }}>
                {plan.highlights.map((note) => (
                  <Paper key={note.id} variant="outlined" sx={{ p: 1.75 }}>
                    <ReleaseNoteItem note={note} detailed onShowMe={showMe} />
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
          {plan.list.length > 0 && (
            <Box component="section">
              <Typography component="h3" sx={SECTION_HEADING_SX}>
                {plan.highlights.length > 0
                  ? t("whats_new.list_heading_more")
                  : t("whats_new.list_heading")}
              </Typography>
              <Box component="ul" sx={LIST_SX}>
                {plan.list.map((note) => (
                  <ReleaseNoteItem key={note.id} note={note} component="li" />
                ))}
              </Box>
            </Box>
          )}
          {(plan.fixes.length > 0 || plan.more > 0) && (
            <Box>
              {plan.fixes.length > 0 && (
                <>
                  <Button
                    size="small"
                    onClick={() => setFixesOpen((v) => !v)}
                    aria-expanded={fixesOpen}
                    aria-controls="whats-new-fixes"
                    startIcon={fixesOpen ? <ExpandLess aria-hidden /> : <ExpandMore aria-hidden />}
                    sx={{ minHeight: 44, px: 1 }}
                  >
                    {fixesOpen
                      ? t("whats_new.fixes_hide")
                      : t("whats_new.fixes_show", { count: plan.fixes.length })}
                  </Button>
                  <Box id="whats-new-fixes" hidden={!fixesOpen} sx={{ mt: 1 }}>
                    <Box component="ul" sx={LIST_SX}>
                      {plan.fixes.map((note) => (
                        <ReleaseNoteItem key={note.id} note={note} component="li" />
                      ))}
                    </Box>
                  </Box>
                </>
              )}
              {plan.more > 0 && (
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                  {t("whats_new.more", { count: plan.more })}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={
          phone
            ? {
                flexDirection: "column-reverse",
                alignItems: "stretch",
                gap: 1,
                px: 2,
                pt: 1.5,
                pb: "calc(12px + env(safe-area-inset-bottom, 0px))",
                "& > :not(style) ~ :not(style)": { ml: 0 },
              }
            : { justifyContent: "space-between", px: 3, py: 1.5 }
        }
      >
        <Button
          onClick={() => onClose(seeAll)}
          fullWidth={phone}
          sx={phone ? { minHeight: 48 } : undefined}
        >
          {t("whats_new.see_all")}
        </Button>
        <Button
          variant="contained"
          onClick={() => onClose()}
          fullWidth={phone}
          sx={phone ? { minHeight: 48 } : undefined}
        >
          {t("whats_new.got_it")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
